import React from 'react';
import PropTypes from 'prop-types';
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider, Typography } from '@mui/material';

/**
 * The right-click menu offering the two spiral inspection directions for one bar.
 *
 * <p>Anchored to the pointer rather than to an element, because the thing being acted on is a
 * candle inside a canvas - there is no DOM node to attach to.
 *
 * <p>The bar's own date is shown as a disabled header. Without it the menu is two verbs with no
 * subject, and on a dense chart the bar under the cursor is genuinely ambiguous - a user who
 * right-clicks one bar and inspects its neighbour has no way to notice.
 *
 * <p>The two directions are offered separately as well as together, and deliberately kept that way.
 * Both at once is the fullest answer but also the busiest chart: a forward ladder can be a dozen
 * rungs, and drawing it under a lineage is a lot to read when the question was only one of them.
 */
export default function SpiralInspectMenu({
  anchorPosition, barDate, onForward, onBackward, onBoth, onAnalytica, onClose,
}) {
  const open = Boolean(anchorPosition);

  return (
    <Menu
      open={open}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition || undefined}
      MenuListProps={{ dense: true }}
    >
      <MenuItem disabled sx={{ opacity: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4 }}>
          {barDate || 'Selected bar'}
        </Typography>
      </MenuItem>
      <Divider />

      {/*
        * CHART ANALYTICA, FIRST.
        *
        * <p>This menu's identity is spiral inspection and this item is not that — it is placed
        * above them because right-click on a bar is the gesture Chart Analytica was specified
        * to be reached by, which makes it the primary action of the gesture now. The spiral
        * items are unchanged below their own divider.
        *
        * <p>Omitted entirely when no handler is supplied, rather than shown disabled: a greyed
        * row invites a reader to work out why, and there is no why to find.
        */}
      {onAnalytica && (
        <>
          <MenuItem onClick={onAnalytica}>
            <ListItemIcon sx={{ minWidth: 30, fontSize: 16 }}>🔬</ListItemIcon>
            <ListItemText
              primary="Chart Analytica"
              secondary="Scan every bar from the oldest up to this one"
            />
          </MenuItem>
          <Divider />
        </>
      )}

      <MenuItem onClick={onForward}>
        <ListItemIcon sx={{ minWidth: 30, fontSize: 16 }}>🔮</ListItemIcon>
        <ListItemText
          primary="Project Φ Forward"
          secondary="Where this bar's ladder points, and what it hit"
        />
      </MenuItem>

      <MenuItem onClick={onBackward}>
        <ListItemIcon sx={{ minWidth: 30, fontSize: 16 }}>📜</ListItemIcon>
        <ListItemText
          primary="Trace Φ Lineage (Backward)"
          secondary="Which earlier turns point at this bar"
        />
      </MenuItem>

      {onBoth && (
        <>
          <Divider />
          <MenuItem onClick={onBoth}>
            <ListItemIcon sx={{ minWidth: 30, fontSize: 16 }}>⟷</ListItemIcon>
            <ListItemText
              primary="Both Directions"
              secondary="The full harmonic picture around this bar"
            />
          </MenuItem>
        </>
      )}
    </Menu>
  );
}

SpiralInspectMenu.propTypes = {
  /** {top, left} in client coordinates, or null when closed. */
  anchorPosition: PropTypes.shape({ top: PropTypes.number, left: PropTypes.number }),
  barDate: PropTypes.string,
  onForward: PropTypes.func.isRequired,
  onBackward: PropTypes.func.isRequired,
  /**
   * Runs both directions at once. Optional, and the item is omitted when absent rather than shown
   * disabled — a greyed-out row invites a reader to work out why, and there is no why to find.
   */
  onBoth: PropTypes.func,
  /**
   * Runs Chart Analytica from bar 0 to this bar. Optional, and the item is OMITTED when absent
   * for the same reason `onBoth` is — a disabled row is a question with no answer.
   */
  onAnalytica: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};
